import { createTheme } from '@mui/material/styles';
import { colors, spacing } from './theme';

/**
 * Theme de MUI de la aplicación, construido a partir de los tokens de `styles/theme.js`.
 *
 * Antes vivía inline en `src/index.js` con valores propios que no tenían relación
 * con los tokens de marca. Centralizarlo acá permite migrar de a poco sin tocar
 * el punto de entrada.
 *
 * @see docs/CONVENTIONS.md sección 7
 */

/**
 * Stack tipográfico. Coincide con el `body` de `index.css`: Inter no se sirve desde
 * ningún CDN, así que el fallback a Arial es real y no debe quitarse.
 */
const FONT_FAMILY = 'Inter, Arial, sans-serif';

export const muiTheme = createTheme({
  palette: {
    /**
     * Verde de marca. Es lo que heredan sin pedirlo el anillo de foco de los
     * TextField, los Button `contained` sin `color`, el indicador de Tabs y los
     * progress sin `sx`. Antes era el azul `#1976d2` del bootstrap de MUI.
     */
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryHover,
      contrastText: colors.textPanelDescription,
    },
    secondary: { main: colors.backgroundLight },
  },
  typography: {
    fontFamily: FONT_FAMILY,
  },
  /**
   * Botón deshabilitado: la paleta es `light`, así que MUI lo pintaba con negro al 26 % (texto y borde):
   * sobre el fondo oscuro de la app «Cancelar» casi desaparecía mientras corre un pedido. Acá, para
   * todos los `Button` de la app: gris y legible (lo que un `sx` propio defina sigue mandando).
   */
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': { color: colors.disabledOnDarkText },
        },
        outlined: {
          '&.Mui-disabled': { color: colors.disabledOnDarkText, borderColor: colors.disabledOnDarkBorder },
        },
        contained: {
          '&.Mui-disabled': { color: colors.disabledOnDarkText, backgroundColor: colors.disabledOnDarkBg },
        },
      },
    },
  },
  // Ambos coinciden con los defaults de MUI, así que derivarlos no cambia nada hoy;
  // el valor está en que la fuente de verdad pasa a ser `theme.js`.
  spacing: spacing.unit,
  shape: {
    borderRadius: parseInt(spacing.borderRadius.sm, 10),
  },
});

/**
 * `zIndex` a propósito no se deriva de `theme.js`: la escala de MUI (modal 1300,
 * tooltip 1500) es interna de sus componentes y pisarla con la nuestra (modal 1000)
 * rompería el apilado de diálogos y tooltips. Los tokens de `theme.js` son para
 * capas propias de la app, no para componentes de MUI.
 */

export default muiTheme;
